import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { getValidToken } from '../utils/token';

interface User {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    is_active: boolean;
    is_superuser: boolean;
    role_id: number | null;
    role_name: string | null;
    permissions: Array<{ page: string; can_access: boolean }> | null;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    fetchUser: () => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchUser = async () => {
        // Reject missing/expired/malformed tokens up front (clears them too),
        // so we never call the API — or render the shell — with a dead token.
        const token = getValidToken();
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }

        try {
            const res = await api.get('/auth/users/me');
            setUser(res.data);
            // Fire-and-forget: if QBO is the active integration and a company is
            // connected, refresh vendors/accounts automatically on login/app-load.
            // The backend no-ops (and throttles) when it isn't applicable.
            api.post('/qbo/auto-sync').catch(() => {});
        } catch (err) {
            console.error('Failed to fetch user profile', err);
            localStorage.removeItem('token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
        window.location.href = '/login';
    };

    useEffect(() => {
        fetchUser();
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, fetchUser, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
